import { Badge, Card } from '@classgo/ui';

import { useT } from '../../i18n';
import { useTenant } from '../../tenant/tenant-provider';

/**
 * Where the guides live: the repository's `docs/guides/` on GitHub until they have a published
 * home (#122). One page per role; the account screen links both, since a person may hold both.
 */
const GUIDES = {
  teacher: 'https://github.com/ClassGO-AI/classgo/blob/develop/docs/guides/teacher.md',
  admin: 'https://github.com/ClassGO-AI/classgo/blob/develop/docs/guides/admin.md',
} as const;

const GuideLink = ({ href, label }: { href: string; label: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex min-h-11 items-center font-medium text-brand-700 underline underline-offset-2 hover:decoration-2"
  >
    {label}
  </a>
);

/**
 * The person's own account: identity, roles and what they may do, and the help entry: the
 * teacher and admin guides (#122). Read-only.
 */
export const AccountScreen = () => {
  const t = useT();
  const { session } = useTenant();
  const fullName = [session.user.givenName, session.user.familyName].filter(Boolean).join(' ');
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Card title={t('account.title')}>
        <dl className="grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">{t('account.name')}</dt>
            <dd className="mt-0.5 font-medium text-gray-900">{fullName || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('account.email')}</dt>
            <dd className="mt-0.5 font-medium text-gray-900">{session.user.email}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('account.roles')}</dt>
            <dd className="mt-1 flex flex-wrap gap-1">
              {session.roles.map((role) => (
                <Badge key={role} tone="gray">
                  {role}
                </Badge>
              ))}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('account.permissions')}</dt>
            <dd className="mt-0.5 font-medium text-gray-900">
              {session.permissions.all
                ? t('account.permissions.all')
                : session.permissions.keys.length}
            </dd>
          </div>
        </dl>
      </Card>
      <Card title={t('account.guides.title')}>
        <p className="text-sm text-gray-600">{t('account.guides.body')}</p>
        <ul className="mt-2 flex flex-col gap-1 text-sm">
          <li>
            <GuideLink href={GUIDES.teacher} label={t('account.guides.teacher')} />
          </li>
          <li>
            <GuideLink href={GUIDES.admin} label={t('account.guides.admin')} />
          </li>
        </ul>
      </Card>
    </div>
  );
};
